/**
 * `/files` — the libraries, as the way into the tree.
 *
 * Libraries are defined in code, not created by users, so this is a fixed set
 * rather than a list with a "new" button. What it is really for is showing each
 * one's **limits** — the content types it accepts, the maximum file size,
 * whether it keeps versions — before somebody drags a 2 GB video at a library
 * that takes PDFs up to 20 MB and finds out from a 400.
 */
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import LayersIcon from '@mui/icons-material/Layers';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { LibraryDto } from '@forsys/shared';
import { AsyncContent } from '../../components/AsyncContent';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useAsync } from '../../hooks/useAsync';
import { listLibraries } from '../../api/resources';
import { formatBytes } from '../../utils/format';

function LibraryCard({ library }: { library: LibraryDto }) {
  const { t } = useTranslation('files');
  const types = library.allowedContentTypes;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea
        component={RouterLink}
        to={`/files/${encodeURIComponent(library.key)}`}
        sx={{ height: '100%', p: 2.5, alignItems: 'flex-start' }}
      >
        <Stack spacing={1.5} sx={{ width: '100%' }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: 'action.selected',
                color: 'primary.main',
              }}
            >
              <LayersIcon />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                {library.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {library.key}
              </Typography>
            </Box>
            <ArrowForwardIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {library.description}
          </Typography>

          <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Chip
              size="small"
              variant="outlined"
              label={t('libraries.upTo', { size: formatBytes(library.maxFileBytes) })}
            />
            <Chip
              size="small"
              variant="outlined"
              color={library.versioned ? 'success' : 'default'}
              icon={library.versioned ? undefined : <HistoryToggleOffIcon />}
              label={
                library.versioned ? t('libraries.versioned') : t('libraries.noVersionHistory')
              }
            />
            {/* An empty list means "anything", not "nothing" — the opposite of
                what an empty chip row would suggest, so it is spelled out. */}
            <Chip
              size="small"
              variant="outlined"
              label={
                types.length === 0
                  ? t('libraries.anyContentType')
                  : types.length <= 2
                    ? types.join(', ')
                    : t('libraries.contentTypeCount', { count: types.length })
              }
              {...(types.length > 2 ? { title: types.join(', ') } : {})}
            />
          </Stack>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

export default function LibrariesPage() {
  const { t } = useTranslation('files');
  const libraries = useAsync(() => listLibraries(), []);

  return (
    <>
      <PageHeader
        title={t('libraries.title')}
        description={t('libraries.description')}
      />

      <AsyncContent state={libraries}>
        {({ libraries: all }) =>
          all.length === 0 ? (
            <EmptyState
              icon={<LayersIcon fontSize="inherit" />}
              title={t('libraries.emptyTitle')}
              description={t('libraries.emptyDescription')}
            />
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
              }}
            >
              {all.map((library) => (
                <LibraryCard key={library.key} library={library} />
              ))}
            </Box>
          )
        }
      </AsyncContent>
    </>
  );
}
